import React, { useState } from 'react';
import PageContainer from 'containers/PageContainer';
import { Redirect, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ChangePasswordView from 'views/ChangePasswordView';
import { Auth } from 'lib/Amplify';
import useMessages from 'hooks/useMessages';
import { setAppBusy } from 'store/actions/system';

import {
  INVALID_PASSWORD_MESSAGE,
  INCORRECT_PASSWORD_MESSAGE,
  PASSWORD_RETYPE_MISMATCH_MESSAGE,
} from 'helpers/copyHelper';

const ChangePasswordContainer = () => {
  const history = useHistory();

  const email = useSelector((state) => state.email);
  const user = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const [error, setError] = useState({});
  const {
    genericFailureMessage,
    passwordResetSuccessMessage,
    clearMessage,
    tooManyAttemptsMessage,
  } = useMessages();
  if (!email) {
    return <Redirect to="/" />;
  }

  function forgotPasswordHandler() {
    dispatch(setAppBusy(true));
    clearMessage();
    Auth.forgotPassword(email)
      .then(() => {
        history.push('/reset_password');
      })
      .catch(() => {
        genericFailureMessage();
      })
      .finally(() => {
        dispatch(setAppBusy(false));
      });
  }

  function handleSubmit({ currentPassword, newPassword, retypedPassword }) {
    // Minimum eight characters, at least one uppercase letter, one lowercase letter and one number
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

    if (!newPassword.match(regex)) {
      setError({
        newPassword: INVALID_PASSWORD_MESSAGE,
      });
      return;
    }

    if (retypedPassword !== newPassword) {
      setError({
        retypedPassword: PASSWORD_RETYPE_MISMATCH_MESSAGE,
      });
      return;
    }

    if (!currentPassword.match(regex)) {
      setError({
        currentPassword: INCORRECT_PASSWORD_MESSAGE,
      });
      return;
    }

    dispatch(setAppBusy(true));
    clearMessage();
    Auth.changePassword(user, currentPassword, newPassword)
      .then(() => {
        passwordResetSuccessMessage();
        history.push('/overview');
      })
      .catch((err) => {
        switch (err.code) {
          case 'InvalidParameterException':
          case 'InvalidPasswordException':
            setError({
              newPassword: INVALID_PASSWORD_MESSAGE,
            });
            break;

          case 'NotAuthorizedException':
            setError({
              currentPassword: INCORRECT_PASSWORD_MESSAGE,
            });
            break;

          case 'LimitExceededException':
            tooManyAttemptsMessage();
            break;

          default:
            genericFailureMessage();
        }
      })
      .finally(() => {
        dispatch(setAppBusy(false));
      });
  }

  function handleChange(name) {
    delete error[name];
    setError(error);
  }

  function handleCancel() {
    history.goBack();
  }

  return (
    <PageContainer hasBack hideUserMenu>
      <ChangePasswordView
        onSubmit={handleSubmit}
        onChange={handleChange}
        onCancel={handleCancel}
        error={error}
        onForgotPassword={forgotPasswordHandler}
      />
    </PageContainer>
  );
};

ChangePasswordContainer.displayName = 'ChangePasswordContainer';

export default ChangePasswordContainer;
